import { 
  Box, 
  Button, 
  Chip, 
  CircularProgress,
  Divider, 
  FormControl,
  Grid, 
  InputLabel,
  MenuItem,
  Paper, 
  Pagination,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography 
} from '@mui/material';
import { useState, useEffect } from 'react';
import api from '../../services/api/axiosConfig';
import SearchIcon from '@mui/icons-material/Search';

interface Order {
  id: string;
  user_id: string;
  order_number: string;
  total_amount: string;
  payment_status: string;
  order_details: {
    partnerId: string;
    partnerName?: string;
    units: number;
    unitPrice: number;
    productId: string;
    version: string;
    promoCode?: string | null;
    email: string;
    phone: string;
  };
  shipping_address: string;
  billing_address: string;
  paid_at: string | null;
  cancelled_at: string | null;
  cancellation_reason: string | null;
  user: {
    id: string;
    email: string;
    name: string;
  };
  created_at: string;
  updated_at: string;
}

interface OrdersResponse {
  success: boolean;
  totalItems: number;
  data: Order[];
  currentPage: number;
  totalPages: number;
  pageSize: number;
}

const AdminDashboardContent = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalOrdersCount, setTotalOrdersCount] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [searchTerm, setSearchTerm] = useState('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState<string>('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');

  useEffect(() => {
    fetchTotalOrdersCount();
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [currentPage, pageSize, sortBy, sortOrder, paymentStatusFilter]);

  const fetchTotalOrdersCount = async () => {
    try {
      const response = await api.get<OrdersResponse>('/partners/orders?page=1&size=1');
      if (response.data.success) {
        setTotalOrdersCount(response.data.totalItems);
      }
    } catch (error) {
      console.error('Failed to fetch total orders count:', error);
    }
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        size: pageSize.toString(),
        sort: sortBy,
        sortOrder: sortOrder,
      });

      if (paymentStatusFilter) {
        params.append('paymentStatus', paymentStatusFilter);
      }

      if (searchTerm) {
        params.append('search', searchTerm);
      }

      const response = await api.get<OrdersResponse>(`/partners/orders?${params.toString()}`);
      
      if (response.data.success) {
        setOrders(response.data.data);
        setTotalPages(response.data.totalPages);
        setTotalItems(response.data.totalItems);
      }
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchOrders();
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  const handlePageSizeChange = (event: any) => {
    setPageSize(parseInt(event.target.value));
    setCurrentPage(1);
  };

  const formatAmount = (amount: string | number) => {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return `$${(numAmount * 100).toFixed(2)}`;
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
      case 'paid':
        return 'success';
      case 'pending':
        return 'warning';
      case 'cancelled':
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };
  return (
    <Box sx={{ flexGrow: 1, p: { xs: 1, sm: 2, md: 3 }, background: '#151A23', minHeight: '100vh', color: '#fff', marginTop: 2 }}>
      {/* Live System Status */}
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>Live System Status</Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{xs: 12, md: 3}}>
          <Paper sx={{ p: 2, background: '#232B3E', borderRadius: 3, position: 'relative' }}>
            <Typography variant="subtitle2" color="#b0b8c1">System Uptime</Typography>
            <Typography variant="h5" color="success.main">99.9%</Typography>
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                borderRadius: 3,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10,
                backdropFilter: 'blur(2px)',
                pointerEvents: 'none',
              }}
            >
              <Typography
                sx={{
                  color: '#9ca3af',
                  fontSize: 18,
                  fontWeight: 'bold',
                  mb: 0.5,
                }}
              >
                Coming Soon
              </Typography>
            </Box>
          </Paper>
        </Grid>
        <Grid size={{xs: 12, md: 3}}>
          <Paper sx={{ p: 2, background: '#232B3E', borderRadius: 3, position: 'relative' }}>
            <Typography variant="subtitle2" color="#b0b8c1">API Health</Typography>
            <Typography variant="h5" color="warning.main">98.5%</Typography>
            <Typography variant="caption" color="error.main">0.2 Failed Endpoints</Typography>
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                borderRadius: 3,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10,
                backdropFilter: 'blur(2px)',
                pointerEvents: 'none',
              }}
            >
              <Typography
                sx={{
                  color: '#9ca3af',
                  fontSize: 18,
                  fontWeight: 'bold',
                  mb: 0.5,
                }}
              >
                Coming Soon
              </Typography>
            </Box>
          </Paper>
        </Grid>
        <Grid size={{xs: 12, md: 3}}>
          <Paper sx={{ p: 2, background: '#232B3E', borderRadius: 3, position: 'relative' }}>
            <Typography variant="subtitle2" color="#b0b8c1">Slack Alerts</Typography>
            <Typography variant="h5">15</Typography>
            <Typography variant="caption" color="#b0b8c1">Last 24 Hours</Typography>
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                borderRadius: 3,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10,
                backdropFilter: 'blur(2px)',
                pointerEvents: 'none',
              }}
            >
              <Typography
                sx={{
                  color: '#9ca3af',
                  fontSize: 18,
                  fontWeight: 'bold',
                  mb: 0.5,
                }}
              >
                Coming Soon
              </Typography>
            </Box>
          </Paper>
        </Grid>
        <Grid size={{xs: 12, md: 3}}>
          <Paper sx={{ p: 2, background: '#232B3E', borderRadius: 3 }}>
            <Typography variant="subtitle2" color="#b0b8c1">Total Orders</Typography>
            <Typography variant="h5" color="primary.main">{totalOrdersCount.toLocaleString()}</Typography>
            <Typography variant="caption" color="#b0b8c1">All Orders</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Botel Network Utilization */}
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>Botel Network Utilization</Typography>
      <Box sx={{ position: 'relative' }}>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{xs: 12, md: 4}}>
            <Paper sx={{ p: 2, background: '#232B3E', borderRadius: 3 }}>
              <Typography variant="subtitle2" color="#b0b8c1">Occupancy Rate <Chip label="Active" size="small" sx={{ ml: 1, background: '#3456D5', color: '#fff' }} /></Typography>
              <Typography variant="h5" color="primary.main">78%</Typography>
              <Typography variant="caption" color="#b0b8c1">Active Botels: 245</Typography><br />
              <Typography variant="caption" color="#b0b8c1">Inactive Botels: 69</Typography>
            </Paper>
          </Grid>
          <Grid size={{xs: 12, md: 4}}>
            <Paper sx={{ p: 2, background: '#232B3E', borderRadius: 3 }}>
              <Typography variant="subtitle2" color="#b0b8c1">Total Revenue MTD <Chip label="Live" size="small" sx={{ ml: 1, background: '#3456D5', color: '#fff' }} /></Typography>
              <Typography variant="h5" color="success.main">$847,392</Typography>
            </Paper>
          </Grid>
          <Grid size={{xs: 12, md: 4}}>
            <Paper sx={{ p: 2, background: '#232B3E', borderRadius: 3 }}>
              <Typography variant="subtitle2" color="#b0b8c1">Guest Activity <Chip label="Active" size="small" sx={{ ml: 1, background: '#3456D5', color: '#fff' }} /></Typography>
              <Typography variant="h5">1,234</Typography>
              <Typography variant="caption" color="#b0b8c1">Check-ins Today: 89</Typography><br />
              <Typography variant="caption" color="#FFD700">Pending Issues: 12</Typography>
            </Paper>
          </Grid>
        </Grid>
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            borderRadius: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
            backdropFilter: 'blur(2px)',
            pointerEvents: 'none',
          }}
        >
          <Typography
            sx={{
              color: '#9ca3af',
              fontSize: 24,
              fontWeight: 'bold',
              mb: 1,
            }}
          >
            Coming Soon
          </Typography>
          <Typography
            sx={{
              color: '#6b7280',
              fontSize: 16,
              textAlign: 'center',
              px: 2,
            }}
          >
            This feature will be available in a future release
          </Typography>
        </Box>
      </Box>

      {/* Order Management */}
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, mt: 4 }}>Order Management</Typography>
      <Paper sx={{ p: 3, background: '#232B3E', borderRadius: 3, mb: 3 }}>
        {/* Filters and Search */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by order number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
              InputProps={{
                endAdornment: (
                  <Button
                    onClick={handleSearch}
                    sx={{ minWidth: 'auto', p: 1 }}
                    size="small"
                  >
                    <SearchIcon sx={{ color: '#fff' }} />
                  </Button>
                ),
                sx: { 
                  background: '#181F2A',
                  color: '#fff',
                  '& input': { color: '#fff' }
                }
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ color: '#b0b8c1' }}>Payment Status</InputLabel>
              <Select
                value={paymentStatusFilter}
                onChange={(e) => setPaymentStatusFilter(e.target.value)}
                sx={{ 
                  background: '#181F2A',
                  color: '#fff',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#3456D5' }
                }}
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ color: '#b0b8c1' }}>Page Size</InputLabel>
              <Select
                value={pageSize}
                onChange={handlePageSizeChange}
                sx={{ 
                  background: '#181F2A',
                  color: '#fff',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#3456D5' }
                }}
              >
                <MenuItem value={10}>10 per page</MenuItem>
                <MenuItem value={20}>20 per page</MenuItem>
                <MenuItem value={50}>50 per page</MenuItem>
                <MenuItem value={100}>100 per page</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <Typography variant="body2" color="#b0b8c1">
                Total: {totalItems}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Orders Table */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ background: '#181F2A' }}>
                <TableCell 
                  sx={{ color: '#fff', fontWeight: 700, cursor: 'pointer' }}
                  onClick={() => {
                    if (sortBy === 'order_number') {
                      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
                    } else {
                      setSortBy('order_number');
                      setSortOrder('ASC');
                    }
                  }}
                >
                  Order Number {sortBy === 'order_number' && (sortOrder === 'ASC' ? '↑' : '↓')}
                </TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Customer</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Partner</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Units</TableCell>
                <TableCell 
                  sx={{ color: '#fff', fontWeight: 700, cursor: 'pointer' }}
                  onClick={() => {
                    if (sortBy === 'total_amount') {
                      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
                    } else {
                      setSortBy('total_amount');
                      setSortOrder('ASC');
                    }
                  }}
                >
                  Amount {sortBy === 'total_amount' && (sortOrder === 'ASC' ? '↑' : '↓')}
                </TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Payment Status</TableCell>
                <TableCell 
                  sx={{ color: '#fff', fontWeight: 700, cursor: 'pointer' }}
                  onClick={() => {
                    if (sortBy === 'created_at') {
                      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
                    } else {
                      setSortBy('created_at');
                      setSortOrder('DESC');
                    }
                  }}
                >
                  Date {sortBy === 'created_at' && (sortOrder === 'ASC' ? '↑' : '↓')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                [...Array(5)].map((_, idx) => (
                  <TableRow key={idx}>
                    <TableCell colSpan={7} sx={{ py: 3 }}>
                      <Skeleton variant="rectangular" height={20} sx={{ bgcolor: '#181F2A', borderRadius: 1 }} />
                    </TableCell>
                  </TableRow>
                ))
              ) : orders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4, border: 'none' }}>
                    <Typography variant="body2" color="#b0b8c1">
                      No orders found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                orders.map((order) => (
                  <TableRow key={order.id} sx={{ '&:hover': { background: '#1C2533' } }}>
                    <TableCell sx={{ color: '#fff' }}>{order.order_number}</TableCell>
                    <TableCell sx={{ color: '#fff' }}>
                      <Box>
                        <Typography variant="body2">{order.user?.name || 'N/A'}</Typography>
                        <Typography variant="caption" color="#b0b8c1">
                          {order.user?.email || order.order_details?.email || 'N/A'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: '#fff' }}>
                      {order.order_details?.partnerName || 'N/A'}
                    </TableCell>
                    <TableCell sx={{ color: '#fff' }}>
                      {order.order_details?.units || 0}
                    </TableCell>
                    <TableCell sx={{ color: '#fff' }}>
                      {formatAmount(order.total_amount)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={order.payment_status}
                        color={getStatusColor(order.payment_status) as any}
                        size="small"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: '#fff' }}>
                      <Typography variant="body2">
                        {new Date(order.created_at).toLocaleDateString()}
                      </Typography>
                      <Typography variant="caption" color="#b0b8c1">
                        {new Date(order.created_at).toLocaleTimeString()}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3 }}>
            <Typography variant="body2" color="#b0b8c1">
              Page {currentPage} of {totalPages}
            </Typography>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              sx={{
                '& .MuiPaginationItem-root': {
                  color: '#fff',
                  '&.Mui-selected': {
                    backgroundColor: '#3456D5',
                  },
                },
              }}
            />
          </Box>
        )}
      </Paper>

      {/* Quick Actions */}
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>Quick Actions</Typography>
      <Box sx={{ position: 'relative' }}>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{xs: 12, md: 3}}>
            <Paper sx={{ p: 2, background: '#232B3E', borderRadius: 3, textAlign: 'center' }}>
              <Button variant="outlined" color="primary" fullWidth>Override Booking</Button>
            </Paper>
          </Grid>
          <Grid size={{xs: 12, md: 3}}>
            <Paper sx={{ p: 2, background: '#232B3E', borderRadius: 3, textAlign: 'center' }}>
              <Button variant="outlined" color="primary" fullWidth>Send Alerts</Button>
            </Paper>
          </Grid>
          <Grid size={{xs: 12, md: 3}}>
            <Paper sx={{ p: 2, background: '#232B3E', borderRadius: 3, textAlign: 'center' }}>
              <Button variant="outlined" color="primary" fullWidth>Payment Adjustments</Button>
            </Paper>
          </Grid>
          <Grid size={{xs: 12, md: 3}}>
            <Paper sx={{ p: 2, background: '#232B3E', borderRadius: 3, textAlign: 'center' }}>
              <Button variant="outlined" color="primary" fullWidth>System Settings</Button>
            </Paper>
          </Grid>
        </Grid>
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            borderRadius: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
            backdropFilter: 'blur(2px)',
            pointerEvents: 'none',
          }}
        >
          <Typography
            sx={{
              color: '#9ca3af',
              fontSize: 24,
              fontWeight: 'bold',
              mb: 1,
            }}
          >
            Coming Soon
          </Typography>
          <Typography
            sx={{
              color: '#6b7280',
              fontSize: 16,
              textAlign: 'center',
              px: 2,
            }}
          >
            This feature will be available in a future release
          </Typography>
        </Box>
      </Box>

      {/* Scheduled Reports & On-Demand Reports */}
      <Box sx={{ position: 'relative' }}>
        <Grid container spacing={2}>
          <Grid size={{xs: 12, md: 6}}>
            <Paper sx={{ p: 2, background: '#232B3E', borderRadius: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold">Scheduled Reports</Typography>
                <Button variant="contained" color="primary" size="small">New Report</Button>
              </Box>
              <Divider sx={{ mb: 2, background: '#333' }} />
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2">Occupancy Analytics</Typography>
                <Typography variant="caption" color="#b0b8c1">Runs every Monday</Typography>
                <Button variant="text" color="primary" size="small" sx={{ float: 'right' }}>Download</Button>
              </Box>
              <Box>
                <Typography variant="body2">Monthly Revenue Report</Typography>
                <Typography variant="caption" color="#b0b8c1">Runs every 1st of month</Typography>
                <Button variant="text" color="primary" size="small" sx={{ float: 'right' }}>Download</Button>
              </Box>
            </Paper>
          </Grid>
          <Grid size={{xs: 12, md: 6}}>
            <Paper sx={{ p: 2, background: '#232B3E', borderRadius: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold">On-Demand Reports</Typography>
                <Button variant="outlined" color="primary" size="small">Filter</Button>
              </Box>
              <Divider sx={{ mb: 2, background: '#333' }} />
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2">Custom Revenue Analysis</Typography>
                  <Typography variant="caption" color="#b0b8c1">CSV, Excel</Typography>
                </Box>
                <Button variant="contained" color="primary" size="small">Generate</Button>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2">Guest Engagement Report</Typography>
                  <Typography variant="caption" color="#b0b8c1">PDF, CSV</Typography>
                </Box>
                <Button variant="contained" color="primary" size="small">Generate</Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            borderRadius: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
            backdropFilter: 'blur(2px)',
            pointerEvents: 'none',
          }}
        >
          <Typography
            sx={{
              color: '#9ca3af',
              fontSize: 24,
              fontWeight: 'bold',
              mb: 1,
            }}
          >
            Coming Soon
          </Typography>
          <Typography
            sx={{
              color: '#6b7280',
              fontSize: 16,
              textAlign: 'center',
              px: 2,
            }}
          >
            This feature will be available in a future release
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default AdminDashboardContent; 